import { useEffect, useState } from "react"
import PropTypes from "prop-types"

import ServiceBrand from "./shared/service_brand"
import SuggestionModal from "shared/components/suggestion_modal"
import { useBgColorContext } from "./context"
import { headerColored } from "./utils/container_background_color"
import { pluralizeStepType } from "shared/lib/utils"
import { trackClickServiceStepSelected } from "./utils/telemetry"
import plusIcon from "images/icons/plus-icon-soft.svg"
import plusIconMobile from "images/icons/plus-icon-soft-mobile.svg"
import proPlusBadge from "images/plan-badges/pro_plus.svg"
import proBadge from "images/plan-badges/pro.svg"

import "./step_selector.scss"
import classNames from "classnames"

const SuggestionCard = ({ stepType, onClick, isDesktop }) => (
  <li styleName="suggestion-card" role="listitem" onClick={onClick}>
    <a title={`Suggest a new ${stepType}`}>
      <span dangerouslySetInnerHTML={{ __html: isDesktop ? plusIcon : plusIconMobile }} />
      <span styleName="title">Suggest a new {stepType}</span>
    </a>
  </li>
)

SuggestionCard.propTypes = {
  stepType: PropTypes.oneOf(["trigger", "action", "query"]).isRequired,
  onClick: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool,
}

export default function StepSelector({ onStepSelect, service, selectedSteps, steps, stepType }) {
  const bgColor = useBgColorContext()
  const [showSuggestionModal, setShowSuggestionModal] = useState(false)
  const isDesktop = window.matchMedia?.(`(min-width: 724px)`)?.matches

  useEffect(() => {
    headerColored(bgColor)

    return () => {
      headerColored("initial")
    }
  }, [bgColor])

  const identifiersFromSelectedSteps = selectedSteps.map(step => step.full_module_name)

  const stepsAvailables = steps
    .filter(step => !identifiersFromSelectedSteps.includes(step.full_module_name))
    .sort((a, b) => a["weight"] - b["weight"])

  const tile = step => {
    const onStepClicked = () => {
      trackClickServiceStepSelected({
        stepIdentifier: step.full_module_name,
        stepType,
      })
      onStepSelect({ ...step, service })
    }

    return (
      <li key={step.id} style={{ backgroundColor: service.brand_color }} role="listitem" onClick={onStepClicked}>
        <a title={`Choose ${stepType}: ${step.name}`} styleName={classNames({ badged: step.tier !== "free" })}>
          <span styleName="title">{step.name}</span>
          <span styleName="description">{step.description}</span>
        </a>
        {step.tier !== "free" && (
          <span
            title={`This ${stepType} has pro features`}
            className="card-badge fixed"
            dangerouslySetInnerHTML={{
              __html: step.tier === "pro" ? proPlusBadge : proBadge,
            }}
          />
        )}
      </li>
    )
  }

  return (
    <>
      {showSuggestionModal && (
        <SuggestionModal
          channelId={service.id}
          onClose={() => setShowSuggestionModal(false)}
          feedbackTqaType={stepType}
          serviceName={service.name}
        />
      )}
      <div styleName="step-selector">
        <section styleName="brand" style={{ backgroundColor: bgColor }}>
          <ServiceBrand title={service.name} image={service.image} />
        </section>
        {stepsAvailables.length > 0 ? (
          <ul role="list">
            {stepsAvailables.map(step => tile(step))}
            <SuggestionCard stepType={stepType} onClick={() => setShowSuggestionModal(true)} isDesktop={isDesktop} />
          </ul>
        ) : (
          <span styleName="empty-list">You have selected all {pluralizeStepType(stepType)} from this service</span>
        )}
      </div>
    </>
  )
}

StepSelector.propTypes = {
  onStepSelect: PropTypes.func.isRequired,
  service: PropTypes.object.isRequired,
  steps: PropTypes.array.isRequired,
  selectedSteps: PropTypes.array,
  stepType: PropTypes.oneOf(["trigger", "action", "query"]).isRequired,
}
